import {
  GRADEBOOK_SETTINGS,
  extractSettingAs,
} from '@macmillan-learning/gradebook-shared';
import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { closeIndividualStudentGradebook } from '../../../../../actions/gradebook';
import { formatDisplayPercentage } from '../../../../../utils/InstructorGradebookHelpers';
import MLButton from '../../../../MLButton/MLButton';
import MLDialog from '../../../../MLDialog/MLDialog';
import MLIcon from '../../../../MLIcon/MLIcon';
import MLLoading from '../../../../MLLoading/MLLoading';
import MLTooltip from '../../../../MLTooltip';
import {
  EXPORT_ACTIVITY_LOG_STRING,
  NO_ACTIVITY_HISTORY_STRING,
} from '../../constants';
import { useAssignments } from '../../hooks/useAssignments';
import useViewportWidth from '../../hooks/useViewportWidth';
import {
  getRoundPercentTypeValue,
  getStudentAssignmentFromCategories,
  handleExportCSV,
  hasOpenedAssignments,
} from '../../utils';
import { CourseTotal } from './CourseTotal';
import s from './Header.module.css';
import { StudentInfo } from './StudentInfo';
import { StudentSelector } from './StudentSelector';

export const Header = ({
  isBrokenSyncReefGrades,
  user,
  gradebookData,
  latestActivityLaunch,
  updateSelectedStudent,
  currentStudent,
  setDropdownOpen,
  searchTerm,
}) => {
  const { isMobile, isDesktop } = useViewportWidth();

  const {
    courseId,
    categories,
    categoryWeights: weights,
    settings,
  } = useSelector((state) => state.gradebook);

  const { course: courseData } = useAssignments({
    searchTerm,
    gradebookData,
    categories,
    weights,
    settings,
  });

  const studentAssignments = getStudentAssignmentFromCategories(
    gradebookData.categoriesData,
  );

  const roundPercent = getRoundPercentTypeValue(settings);
  const { grade, pointsEarned, pointsPossible } = courseData;
  const courseTotalPercent = formatDisplayPercentage(
    grade * 100,
    roundPercent || GRADEBOOK_SETTINGS.roundPercentHundredths,
  );

  const dispatch = useDispatch();

  const showPercentSetting = settings.find(
    (setting) => setting.key === 'showPercent',
  )?.value;
  const isShowPercent = showPercentSetting !== 'false';

  const [isExportDialogOpen, setExportDialogOpen] = useState(false);
  const [isExporting, setIsExporting] = useState(false);

  const assignmentsOpened =
    latestActivityLaunch || hasOpenedAssignments(studentAssignments);

  const tooltipTitle = assignmentsOpened
    ? EXPORT_ACTIVITY_LOG_STRING
    : NO_ACTIVITY_HISTORY_STRING;

  const handleExportDialogOpen = () => setExportDialogOpen(true);
  const handleExportDialogClose = () => {
    setExportDialogOpen(false);
    setIsExporting(false); // Reset loading state when dialog is closed
  };

  const handleExportConfirm = async () => {
    setIsExporting(true);
    await handleExportCSV(courseId, currentStudent);
    setIsExporting(false);
    setExportDialogOpen(false);
  };

  const handleCancelExport = () => {
    setIsExporting(false); // Cancel the loading/exporting process
    setExportDialogOpen(false); // Close the dialog automatically
  };

  const studentIdRequired = extractSettingAs(
    settings,
    'requireStudentId',
    (x) => x.value,
  );
  const isStudentIdRequiredChecked =
    (studentIdRequired ?? 'false').toString() === 'true';

  const renderBackToClassGradebook = () => (
    <button
      className={s.backToGradebookLink}
      onClick={() => dispatch(closeIndividualStudentGradebook())}
    >
      Back to Class Gradebook
    </button>
  );

  const renderLeftSection = () => (
    <div className={s.isrLeftSection}>
      {!isMobile && (
        <div className={s.isrIconPosition}>
          <button
            className={s.isrIconWrapper}
            aria-label="Navigate to Class Gradebook"
            data-test-id="isg-return-to-class-gb-button"
            onClick={() => dispatch(closeIndividualStudentGradebook())}
          >
            <MLTooltip
              content={
                <span style={{ fontSize: '14px', fontWeight: 400 }}>
                  Class Gradebook
                </span>
              }
            >
              <MLIcon className={s.mortarBoardIcon} type="mortar_board" />
            </MLTooltip>
          </button>
        </div>
      )}
      <div className={s.isrStudentSection}>
        <StudentSelector
          className={s.studentSelector}
          courseId={courseId}
          currentStudent={currentStudent}
          updateSelectedStudent={updateSelectedStudent}
          setDropdownOpen={setDropdownOpen}
        />
        <StudentInfo
          email={currentStudent?.email}
          thirdPartyUserId={currentStudent?.thirdPartyUserId}
          isBrokenSyncReefGrades={isBrokenSyncReefGrades}
          user={user}
          isDesktop={isDesktop}
          hidden={!currentStudent}
          isStudentIdRequiredChecked={isStudentIdRequiredChecked}
        />
      </div>
    </div>
  );

  const renderRightSection = () => (
    <div className={s.isrRightSection}>
      <CourseTotal
        courseTotalPointsEarned={pointsEarned}
        courseTotalPointsPossible={pointsPossible}
        courseTotalPercent={courseTotalPercent}
        courseTotalGrade={grade}
        latestActivityLaunch={latestActivityLaunch}
        showPercent={isShowPercent}
        isMobile={isMobile}
      />
      <div className={s.isrButtonGroup}>
        <button
          className={`${s.activityLogButton} ${
            assignmentsOpened ? '' : s.buttonDisabled
          }`}
          onClick={handleExportDialogOpen}
          disabled={!assignmentsOpened}
          data-test-id="isg-export-activity-log"
        >
          <MLTooltip
            placement={assignmentsOpened ? 'bottom' : 'top-start'}
            content={
              <span style={{ fontSize: '14px', fontWeight: 400 }}>
                {tooltipTitle}
              </span>
            }
          >
            <div className={s.exportButton}>
              <MLIcon type="export" className={s.headerBtnIcon} />
              <span className={s.exportButtonText}>
                {EXPORT_ACTIVITY_LOG_STRING}
              </span>
            </div>
          </MLTooltip>
        </button>
      </div>
    </div>
  );

  return (
    <div className={s.isrHeader}>
      {isMobile && renderBackToClassGradebook()}
      {renderLeftSection()}
      {renderRightSection()}
      <MLDialog
        className={s.modalWindow}
        show={isExportDialogOpen}
        onClose={handleExportDialogClose}
        title="Export Student Activity Log"
        titleClassName={s.dialogTitle}
        titleTextClassName={s.dialogTitleText}
        actionsClassName={
          isExporting ? s.dialogActionsExporting : s.dialogActions
        }
        overlayClassName={s.exportDialogOverlay}
        hideActionsBorder={true}
        actions={
          isExporting
            ? [
                <MLButton
                  key={1}
                  btnType="secondaryRed"
                  className={s.btnCancelExport}
                  onClick={handleCancelExport}
                >
                  Cancel Export
                </MLButton>,
              ]
            : [
                <MLButton
                  key={1}
                  btnType="secondary"
                  onClick={handleExportDialogClose}
                  testId="isg-export-activity-log-cancel"
                >
                  Cancel
                </MLButton>,
                <MLButton
                  key={2}
                  btnType="primary"
                  onClick={handleExportConfirm}
                  autoFocus
                  testId="isg-export-activity-log-confirmation"
                >
                  Export
                </MLButton>,
              ]
        }
      >
        {isExporting ? (
          <div className={s.loadingText} data-test-id="export_modal_loading">
            <MLLoading />
            <p>
              We're exporting this student's activity log. <br />
              This could take a few minutes to complete.
            </p>
          </div>
        ) : (
          <p className={s.exportDialogText}>
            The Student Activity Log records the timestamp of every activity
            launch by a student. The log is exported as a CSV file.
          </p>
        )}
      </MLDialog>
    </div>
  );
};
